import clsx from 'clsx';
import { Order } from 'domains/profile';
import { CurrencySymbol, OrderStatus, OrderType } from 'models';
import { FEButton } from 'components';
import style from './style.module.css';
import { Product } from './components';
import { getTime } from '../../../..';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import OrderProgress from '../../../../../../../../../../shared/ui-kit/components/OrderProgress/OrderProgress';
import { useOptionalProfileHeaderActions } from 'pages/ProfilePage/components/ProfileHeaderContext';
import { useEffect } from 'react';
import { getOrderStatuses } from 'models/OrderStatuses';

const Content = ({
  order,
  onRepeatOrder,
  onRetryPayment,
  onClose,
  currency,
}: {
  order: Order;
  onRepeatOrder: ({ order }: { order: Order }) => void;
  onRetryPayment: ({ order }: { order: Order }) => void;
  onClose: () => void;
  currency: CurrencySymbol;
}) => {
  const { t } = useTranslation();

  const profileHeaderActions = useOptionalProfileHeaderActions();

  const orderTitle = t('orders.orderNumberLabel', {
    orderNumber: order.number,
  });
  
  useEffect(() => {
    if (!profileHeaderActions) {
      return;
    }

    profileHeaderActions.setHeader({
      title: orderTitle,
      onBack: onClose,
    });

    return () => {
      profileHeaderActions.resetHeader();
    };
  }, [orderTitle, onClose, profileHeaderActions]);

  const statuses = getOrderStatuses(t);

  const getStatusHeaderTitle = (status: OrderStatus): string => {
    switch (status) {
      case 'AWAITING_PAYMENT':
        return 'Ожидает оплаты';

      case 'PAYED':
        return 'Оплачен';
      case 'PROCESSING':
      case 'IN_PROGRESS':
        return 'Отдых уже близко';

      case 'COOKING':
        return 'Готовим';

      case 'ON_WAY':
      case 'WAIT':
        return 'Всё приготовили';

      case 'DONE':
        return 'Отдыхай с удовольствием';

      case 'CANCELLED':
        return 'Заказ отменен';

      default:
        return 'Отдых уже близко';
    }
  };

  const statusHeaderTitle = getStatusHeaderTitle(order.status);

  const getStatus = (
    type: OrderType,
    status: OrderStatus,
    dateTimeTo: string,
  ) => {
    if (status !== 'CANCELLED' && moment(dateTimeTo).isAfter(moment(), 'day')) {
      return t('orders.statuses.PREORDER');
    }
    return statuses[type][status];
  };

  const getColor: Record<OrderStatus, string> = {
    PROCESSING: style.orange,
    CANCELLED: style.red,
    COOKING: style.orange,
    DONE: style.green,
    IN_PROGRESS: style.orange,
    ON_WAY: style.orange,
    WAIT: style.orange,
    AWAITING_PAYMENT: style.orange,
    PAYED: style.orange,
  };
  return (
    <>
      <div className={style.root}>
        {/* <div className={style.header}>
          <button
            type='button'
            tabIndex={0}
            className='button-arrowBack'
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }}
          />

          <h1 className={style.title}>
            {t('orders.orderNumberLabel', {
              orderNumber: order.number,
            })}
          </h1>
        </div> */}

        <div className={style.layout}>
          <div className={style.cart}>
            <div className={style.cartList}>
              {order.dishes.map((dish) => (
                <Product
                  currency={currency}
                  type='DISH'
                  product={dish}
                  key={`${dish.product.id}-${dish.base.id}`}
                />
              ))}

              {order.constructors.map((constructor) => (
                <Product
                  currency={currency}
                  type='CONSTRUCTOR'
                  product={constructor}
                  key={`${constructor.product.id}-${constructor.base.id}`}
                />
              ))}
            </div>
          </div>

          <aside className={style.info}>
            <div className={style.statusHeader}>
              <p className={style.statusHeaderTitle}>{statusHeaderTitle}</p>
              <div className={style.statusBlock}>
                <p className={style.statusTitle}>
                  {order.status === 'DONE' || order.status === 'CANCELLED'
                    ? statuses[order.type][order.status]
                    : getStatus(
                      order.type,
                      order.status,
                      order.receiptInformation.dateTimeTo,
                    )}
                </p>

                {/* <p className={style.statusDescription}>
                {t('orders.statusLabel')}
              </p> */}
              </div>
            </div>

            <div className={style.orederProgressContainer}>
              <OrderProgress type={order.type} status={order.status} />

            </div>
            <div className={style.data}>
              <div className={style.dataItem}>
                <p className='caption-1'>
                  {order.type === 'COURIER'
                    ? order.status === 'DONE'
                      ? t('orders.statusesLabels.courier.DONE')
                      : order.status === 'CANCELLED'
                        ? t('orders.statusesLabels.CANCELLED')
                        : t('orders.statusesLabels.courier.rest')
                    : order.status === 'DONE'
                      ? t('orders.statusesLabels.goToPlace.DONE')
                      : order.status === 'CANCELLED'
                        ? t('orders.statusesLabels.CANCELLED')
                        : t('orders.statusesLabels.goToPlace.rest')}
                </p>

                <p>
                  {order.status === 'CANCELLED'
                    ? getTime(order.createDatetime, 0, order.status)
                    : order.status === 'DONE'
                      ? getTime(order.closeDatetime, 0, order.status)
                      : getTime(
                        order.receiptInformation.dateTimeTo,
                        order.receiptInformation.deliveryTimeErrorInMinutes,
                        order.status,
                        true,
                      )}
                </p>
              </div>

              <div className={style.dataItem}>
                <p className='caption-1'>
                  {order.type === 'COURIER'
                    ? t('orders.captionCourier')
                    : t('orders.captionGoToPlace')}
                </p>

                <p>
                  {order.type === 'COURIER'
                    ? order.receiptInformation.receivingAddress.address
                    : order.receiptInformation.receivingRestaurant.name}
                </p>
              </div>
            </div>

            <div className={style.pricing}>
              {order.promoCode && (
                <div className={style.promoCode}>
                  <div className={style.pricingItem}>
                    <p>Промокод:</p>
                    <p>{order.promoCode}</p>
                  </div>

                  <p className={style.promoCodeDescription}>
                    “Название” добавлено в корзину
                  </p>
                </div>
              )}

              <div className={style.pricingItem}>
                <p>Товары:</p>
                <p>{`${order.price.cart} ${currency}`}</p>
              </div>

              <div className={style.pricingItem}>
                <p>Скидка:</p>
                <p className={style.discount}>
                  {`${order.price.discount ?? 0} ${currency}`}
                </p>
              </div>

              {order.type === 'COURIER' && (
                <div className={style.pricingItem}>
                  <p>Доставка:</p>
                  <p>{`${order.price.delivery ?? 0} ${currency}`}</p>
                </div>
              )}

              <div className={style.pricingDivider} />

              {/* <div className={style.pricingItem}>
                <p>Икринки:</p>


                <p className={style.caviarBadge}>{`+100`}</p>

              </div> */}

              <div className={clsx(style.pricingItem, style.total)}>
                <p>Итого:</p>
                <p>{`${order.price.total} ${currency}`}</p>
              </div>
            </div>

            {order.status === 'AWAITING_PAYMENT' ? (
              <FEButton onClick={() => onRetryPayment({ order })}>
                {t('orders.retryPayment.button')}
              </FEButton>
            ) : (
              <FEButton onClick={() => onRepeatOrder({ order })}>
                {t('orders.details.repeatOrderButton')}
              </FEButton>
            )}
          </aside>
        </div>
      </div>

    </>
  );
};

export default Content;
